import React, { useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import styles from "./CygnusFormCanvas.module.css";
import { FieldProps, FieldVariant, SectionLayout, SectionVariant } from "../../../InputFieldsController/InputFieldsController.utils";
import InputFieldsController from "../../../InputFieldsController/InputFieldsController";
import CustomButton from "../../../../../pages/reusable_components/custom_button_component/Button";
import * as Icon from "react-bootstrap-icons";
import { isUnmappedSystemField } from "../../../CygnusForm/components/SystemFieldDrawer/SystemFieldDrawer.utils";

/**
 * CygnusFormCanvas (Builder View)
 *
 * Purpose:
 * - Render form pages and sections for the builder.
 * - Always keep hidden fields editable: we render them in a "ghost" state with a "Hidden" badge,
 *   so they can be selected and modified even if `field.show === false`.
 * - Provide an Eye/EyeSlash toggle to optionally hide hidden fields for a tidier preview,
 *   without losing access permanently.
 *
 * Key behaviors:
 * - Groups fields into pages via `pageBreak`, and into sections via section flags.
 * - In builder mode, hidden fields are forced visible at render time (`show: true`) so the
 *   InputFieldsController doesn't suppress them.
 * - Hidden fields are visually dimmed and labeled, but fully selectable.
 * - Toggle `showHidden` controls whether ghosted hidden fields appear in the canvas at all.
 */

interface CygnusFormCanvasProps {
  fields: FieldProps[];
  selectedFieldId: string | null;
  onSelectField: (id: string | null) => void;
  fieldVariant?: FieldVariant;
  sectionVariant?: SectionVariant;
  sectionLayout?: SectionLayout;
  onResolveSystemField?: (id: string) => void;
}

const CygnusFormCanvas: React.FC<CygnusFormCanvasProps> = ({
  fields,
  selectedFieldId,
  onSelectField,
  fieldVariant,
  sectionVariant,
  sectionLayout,
  onResolveSystemField,
}) => {
  const [viewMode, setViewMode] = useState<"all" | "paginated">("all");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [showHidden, setShowHidden] = useState<boolean>(true); // <— NEW: show hidden fields by default (builder-friendly)

  // Group fields into pages using pageBreak
  const pages: FieldProps[][] = [];
  let currentPageGroup: FieldProps[] = [];

  fields.forEach((field) => {
    if (field.pageBreak && currentPageGroup.length > 0) {
      pages.push(currentPageGroup);
      currentPageGroup = [];
    }
    currentPageGroup.push(field);
  });
  if (currentPageGroup.length > 0) {
    pages.push(currentPageGroup);
  }

  const handlePrev = () => setCurrentPage((prev) => Math.max(prev - 1, 0));
  const handleNext = () =>
    setCurrentPage((prev) => Math.min(prev + 1, pages.length - 1));

  // Reset currentPage if it’s out of bounds after reordering
  useEffect(() => {
    if (currentPage >= pages.length) {
      setCurrentPage(Math.max(pages.length - 1, 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages.length]);

  // ——— Section collector (unchanged logic) ———
  const collectSections = (fs: FieldProps[]) => {
    const output: {
      isSection: boolean;
      fields: FieldProps[];
      sectionName?: string;
      sectionStyles?: any;
    }[] = [];

    let buffer: FieldProps[] = [];
    let inSection = false;
    let sectionMeta: { name?: string; styles?: any } = {};

    for (const field of fs) {
      if (field.sectionStart) {
        if (buffer.length > 0 && !inSection) {
          // Only flush if the previous section was properly closed (or we're before
          // any section). If inSection is still true, the previous section never got
          // a sectionEnd — absorb its buffer into the new section instead.
          output.push({
            isSection: inSection,
            fields: buffer,
            sectionName: sectionMeta.name,
            sectionStyles: sectionMeta.styles,
          });
          buffer = [];
        }
        inSection = true;
        sectionMeta = { name: field.sectionName, styles: field.sectionStyles };
      }

      buffer.push(field);

      if (field.sectionEnd && inSection) {
        output.push({
          isSection: true,
          fields: buffer,
          sectionName: sectionMeta.name,
          sectionStyles: sectionMeta.styles,
        });
        buffer = [];
        inSection = false;
        sectionMeta = {};
      }
    }

    if (buffer.length > 0) {
      output.push({
        isSection: inSection,
        fields: buffer,
        sectionName: sectionMeta.name,
        sectionStyles: sectionMeta.styles,
      });
    }

    return output;
  };

  // ——— Single-field renderer that keeps hidden fields editable ———
  const renderOneField = (field: FieldProps) => {
    const originallyHidden = field.show === false;
    const unmapped = isUnmappedSystemField(field);

    // Respect the "hide hidden" toggle
    if (originallyHidden && !showHidden) return null;

    // Force visible at render-time so InputFieldsController doesn't suppress it
    const fieldForRender: FieldProps = {
      ...field,
      show: true,
      disabled: false,
      readOnly: false,
      selected: field.id === selectedFieldId,
      systemStatus: unmapped ? "unmapped" : field.systemStatus,
      onResolveSystemField: unmapped ? onResolveSystemField : undefined,
    };

    const content = (
      <InputFieldsController
        field={fieldForRender}
        handleChange={() => {}}
        handleBlur={() => {}}
        handleSelect={() => {
          if (field.id !== selectedFieldId) onSelectField(field.id);
        }}
      />
    );

    // Wrap hidden ones in a ghost container with a badge
    if (originallyHidden) {
      return (
        <div key={field.id} className={styles.hiddenFieldWrapper}>
          <div className={styles.hiddenBadge}>
            <Icon.EyeSlashFill className={styles.hiddenBadgeIcon} />
            Hidden
          </div>
          {content}
        </div>
      );
    }

    return <React.Fragment key={field.id}>{content}</React.Fragment>;
  };

  const renderBlocks = (fs: FieldProps[]) => {
    const blocks = collectSections(fs);
    const resolvedSectionVariant = sectionVariant ?? "default";
    const effectiveLayout = resolvedSectionVariant === "flat" ? "plain" : (sectionLayout ?? "plain");
    let sectionCount = 0;
    const getUsableSectionColor = (value?: string) =>
      value && value !== "transparent" ? value : undefined;

    return blocks.map((block, blockIndex) => {
      if (!block.isSection) {
        return block.fields.map((field) => (
          <React.Fragment key={field.id}>
            {field.breakLine && <div className={styles.breakLineSpacer} />}
            {renderOneField(field)}
          </React.Fragment>
        ));
      }

      sectionCount++;
      const isFlatVariant = resolvedSectionVariant === "flat";
      const isOutlinedVariant = resolvedSectionVariant === "outlined";
      const sectionBackgroundColor = getUsableSectionColor(block.sectionStyles?.backgroundColor);
      const sectionBorderColor = getUsableSectionColor(block.sectionStyles?.borderColor);
      const bg = isFlatVariant
        ? undefined
        : (sectionBackgroundColor ??
          (isOutlinedVariant ? "#ffffff" : "#f9f9f9"));
      const borderColor = isFlatVariant
        ? undefined
        : (sectionBorderColor ??
          (isOutlinedVariant ? "#d1d5db" : "#cccccc"));
      const titleColor = block.sectionStyles?.titleColor ??
        (isOutlinedVariant ? "#374151" : "#333333");
      const borderWidth = isOutlinedVariant ? 1.5 : 1;
      const sectionBorder = !isFlatVariant && borderColor
        ? `${borderWidth}px solid ${borderColor}`
        : undefined;
      const sectionName = block.sectionName ?? `Section ${blockIndex + 1}`;
      const fieldContent = block.fields.map((field) => (
        <React.Fragment key={field.id}>
          {field.breakLine && <div className={styles.breakLineSpacer} />}
          {renderOneField(field)}
        </React.Fragment>
      ));

      if (effectiveLayout === "banner") {
        return (
          <div
            key={`section-${blockIndex}`}
            className={styles.sectionWrapper}
            data-layout="banner"
            style={{ border: sectionBorder }}
          >
            <div className={styles.sectionBannerHeader} style={{ backgroundColor: bg, color: titleColor }}>
              {sectionName}
            </div>
            <div className={styles.sectionBannerBody}>{fieldContent}</div>
          </div>
        );
      }

      if (effectiveLayout === "accent") {
        return (
          <div
            key={`section-${blockIndex}`}
            className={`${styles.sectionWrapper} ${styles.sectionAccent}`}
            data-layout="accent"
            style={{ borderLeftColor: borderColor, backgroundColor: bg }}
          >
            <div
              className={styles.sectionAccentHeader}
              style={{ background: `linear-gradient(to right, ${borderColor ?? "#BFDBFE"}30, transparent)` }}
            >
              <span className={styles.sectionAccentDot} style={{ backgroundColor: borderColor }} />
              <span className={styles.sectionAccentTitle} style={{ color: titleColor }}>{sectionName}</span>
            </div>
            <div className={styles.sectionBody}>{fieldContent}</div>
          </div>
        );
      }

      if (effectiveLayout === "stepped") {
        return (
          <div
            key={`section-${blockIndex}`}
            className={`${styles.sectionWrapper} ${styles.sectionStepped}`}
            data-layout="stepped"
            style={{
              backgroundColor: bg,
              border: sectionBorder,
            }}
          >
            <div className={styles.sectionSteppedHeader}>
              <div className={styles.sectionStepBadge} style={{ backgroundColor: borderColor ?? titleColor, color: "#fff" }}>
                {sectionCount}
              </div>
              <span className={styles.sectionStepTitle} style={{ color: titleColor }}>{sectionName}</span>
            </div>
            <div className={styles.sectionBody}>{fieldContent}</div>
          </div>
        );
      }

      if (effectiveLayout === "gradient") {
        return (
          <div
            key={`section-${blockIndex}`}
            className={styles.sectionWrapper}
            data-layout="gradient"
            style={{ border: sectionBorder }}
          >
            <div
              className={styles.sectionGradientHeader}
              style={{
                background: `linear-gradient(135deg, ${titleColor ?? "#1E3A8A"} 0%, ${borderColor ?? "#BFDBFE"} 100%)`,
              }}
            >
              {sectionName}
            </div>
            <div className={styles.sectionGradientBody} style={{ backgroundColor: bg }}>{fieldContent}</div>
          </div>
        );
      }

      return (
        <div
          key={`section-${blockIndex}`}
          className={styles.sectionWrapper}
          data-layout="plain"
          style={{
            backgroundColor: bg,
            border: sectionBorder,
          }}
        >
          <div className={styles.sectionHeader} style={{ color: titleColor }}>{sectionName}</div>
          <div className={styles.sectionBody}>{fieldContent}</div>
        </div>
      );
    });
  };

  // Count hidden for small UX hint
  const hiddenCount = useMemo(
    () => fields.filter((f) => f.show === false).length,
    [fields]
  );

  return (
    <Form
      className={styles.formRoot}
      data-field-variant={fieldVariant ?? "default"}
      data-section-variant={sectionVariant ?? "default"}
    >
      {(pages.length > 1 || hiddenCount > 0) && (
        <div className={styles.viewToggleRow}>
          {pages.length > 1 && (
            <div className={styles.viewToggle}>
              {viewMode === "all" ? (
                <Icon.Stack
                  className={styles.controlButton}
                  onClick={() => setViewMode("paginated")}
                  title="Switch to Paginated View"
                />
              ) : (
                <Icon.TvFill
                  className={styles.controlButton}
                  onClick={() => setViewMode("all")}
                  title="Show All Pages"
                />
              )}
            </div>
          )}

          {hiddenCount > 0 && (
            <div className={styles.hiddenToggle}>
              {showHidden ? (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setShowHidden(false)}
                  title="Temporarily hide hidden fields"
                >
                  <Icon.EyeSlashFill /> Hide Hidden ({hiddenCount})
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setShowHidden(true)}
                  title="Show hidden fields for editing"
                >
                  <Icon.EyeFill /> Show Hidden ({hiddenCount})
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.formCanvas}>
        {viewMode === "all" ? (
          pages.map((pageFields, pageIndex) => (
            <div key={`page-${pageIndex}`} className={styles.pageSection}>
              <div className={styles.pageHeader}>
                {pageFields.find(
                  (f, i) => f.pageBreak || (i === 0 && pageIndex === 0)
                )?.pageName || `Page ${pageIndex + 1}`}
              </div>
              <div className={styles.pageContent}>{renderBlocks(pageFields)}</div>
            </div>
          ))
        ) : (
          <div className={styles.pageSection}>
            <div className={styles.paginationControls}>
              <CustomButton
                onclick={handlePrev}
                disabled={currentPage === 0}
                content="Previous"
              />
              <div className={styles.pageHeader}>
                {pages[currentPage]?.find(
                  (f, i) => f.pageBreak || (i === 0 && currentPage === 0)
                )?.pageName || `Page ${currentPage + 1}`}
              </div>
              <CustomButton
                onclick={handleNext}
                disabled={currentPage === pages.length - 1}
                content="Next"
              />
            </div>
            <div className={styles.pageContent}>
              {renderBlocks(pages[currentPage] || [])}
            </div>
          </div>
        )}
      </div>
    </Form>
  );
};

export default CygnusFormCanvas;
